import { get, has, merge } from 'lodash';
import {
  CHART_COLORS,
  VEGA_SPEC_AXIS_SETTINGS,
  CHART_COLORS_SEQUENTIAL,
} from 'constants/chart-spec';
import { CHART_CATEGORIES } from 'constants/chart-categories';
import { generateVegaLabelTextExpression } from 'utils/chart/vega-expressions';
import { generateVegaTooltip } from 'utils/chart/tooltips';
import {
  generateVegaSortParams,
  generateVegaTypeCoercionTransforms,
  generateVegaSortingAndLimitingTransforms,
} from 'utils/chart/transforms';
import { getScaleMinMaxProps } from 'utils/chart/scale-min-max';

export default {
  name: 'Column Colored',
  displayName: 'Colored',
  order: 3,
  flavour: 'discrete-quantitative-series',
  category: CHART_CATEGORIES.COLUMN,
  iconValue: 'column-color',
  channels: [
    {
      name: 'x',
      displayName: 'X Axis',
      required: false,
      channelType: 'category',
      intent: 'category-axis',
    },
    {
      name: 'y',
      displayName: 'Y Axis',
      required: true,
      channelType: 'aggregation',
      intent: 'value-axis',
    },
    {
      name: 'color',
      displayName: 'Color',
      required: true,
      channelType: 'aggregation',
      intent: 'quantitative-series',
    },
  ],
  specFn: (encodings, customisations) => {
    const sortParams = generateVegaSortParams(encodings);
    const logScaleOnValueAxis = get(
      customisations.axes,
      'y.logScale.enabled',
      false
    );
    const valueAxisProps = getScaleMinMaxProps(
      customisations,
      'y',
      logScaleOnValueAxis
    );
    const color = get(
      customisations.options.colorContinuous,
      'value.color',
      CHART_COLORS_SEQUENTIAL[0]
    );
    const reverseColor = get(
      customisations.options.colorContinuous,
      'value.reverse',
      false
    );
    return {
      autosize: {
        type: 'fit',
        contains: 'padding',
      },
      style: 'cell',
      data: [
        {
          name: 'transformedData',
          source: 'values',
          transform: [
            ...generateVegaTypeCoercionTransforms('y'),
            ...generateVegaSortingAndLimitingTransforms(encodings),
            ...(!encodings.color
              ? []
              : [
                  {
                    type: 'filter',
                    expr: 'datum["color"] !== null && !isNaN(datum["color"])',
                  },
                ]),
          ],
        },
      ],
      marks: [
        {
          name: 'marks',
          type: 'rect',
          style: ['bar'],
          from: { data: 'transformedData' },
          encode: {
            update: {
              x: { scale: 'x', field: 'x' },
              y: { scale: 'y', field: 'y' },
              y2: {
                scale: 'y',
                value: logScaleOnValueAxis ? valueAxisProps.domainMin : 0,
              },
              width: { scale: 'x', band: true },
              tooltip: {
                signal: generateVegaTooltip(encodings, customisations),
              },
              fill: encodings.color
                ? { scale: 'color', field: 'color' }
                : { value: CHART_COLORS[0] },
            },
          },
        },
        ...(!get(customisations.options, 'dataValueLabels.enabled')
          ? []
          : [
              {
                type: 'text',
                name: 'valueLabels',
                from: { data: 'transformedData' },
                encode: {
                  enter: {
                    text: {
                      signal: generateVegaLabelTextExpression(
                        encodings.y,
                        customisations.channels.y,
                        { field: 'y' }
                      ),
                    },
                    x: {
                      scale: 'x',
                      field: 'x',
                      offset: { signal: "bandwidth('x') / 2" },
                    },
                    y: { scale: 'y', field: 'y' },
                    align: { value: 'center' },
                    dy: { value: -3 },
                  },
                },
              },
            ]),
      ],
      scales: [
        {
          name: 'x',
          type: 'band',
          domain: { data: 'transformedData', field: 'x', sort: sortParams.x },
          range: [0, { signal: 'width' }],
          paddingInner: 0.1,
          paddingOuter: 0.05,
        },
        {
          name: 'y',
          type: logScaleOnValueAxis ? 'log' : 'linear',
          domain: { data: 'transformedData', field: 'y' },
          range: [{ signal: 'height' }, 0],
          zero: !logScaleOnValueAxis,
          ...valueAxisProps,
        },
        {
          name: 'color',
          type: 'sequential',
          domain: { data: 'transformedData', field: 'color' },
          range: { scheme: color },
          nice: false,
          zero: false,
          reverse: reverseColor,
        },
      ],
      axes: [
        merge({}, VEGA_SPEC_AXIS_SETTINGS, {
          scale: 'x',
          orient: 'bottom',
          grid: false,
          title: get(encodings, 'x.axis.title', ''),
          encode: {
            labels: {
              update: {
                angle: { value: 270 },
                align: { value: 'right' },
                baseline: { value: 'middle' },
                text: {
                  signal: generateVegaLabelTextExpression(
                    encodings.x,
                    customisations.channels.x
                  ),
                },
              },
            },
          },
        }),
        merge({}, VEGA_SPEC_AXIS_SETTINGS, {
          scale: 'y',
          orient: 'left',
          grid: true,
          title: get(encodings, 'y.axis.title', ''),
          encode: {
            labels: {
              update: {
                text: {
                  signal: generateVegaLabelTextExpression(
                    encodings.y,
                    customisations.channels.y,
                    { withDecimals: false }
                  ),
                },
              },
            },
          },
        }),
      ],
      legends: !has(encodings, 'color.legend.title')
        ? undefined
        : [
            {
              title: get(encodings, 'color.legend.title'),
              type: 'gradient',
              fill: 'color',
              encode: {
                labels: {
                  update: {
                    text: {
                      signal: generateVegaLabelTextExpression(
                        encodings.color,
                        customisations.channels.color,
                        { withDecimals: false }
                      ),
                    },
                  },
                },
              },
            },
          ],
    };
  },
};
